import { API } from '../Api';

// Clan (War Clan) — Phase 1a. Đi qua group-service. Response bọc { httpCode, message, data }.
const BASE = '/v1/group-service/clan';

export type ClanRole = 0 | 1 | 2; // KING=0, CAPTAIN=1, MEMBER=2
export type ClanMemberStatus = 0 | 1 | 2; // ACTIVE=0, PENDING=1, BANNED=2
export type ClanStatus = 0 | 1; // ACTIVE=0, DISBANDED=1

export const CLAN_ROLE_LABEL: Record<number, string> = {
  0: 'King',
  1: 'Captain',
  2: 'Thành viên',
};

export interface ClanMember {
  userId: string;
  role: ClanRole;
  status: ClanMemberStatus;
  joinDate: string;
  tenureSince: string;
  seasonContribution: number;
}

export interface Clan {
  id: string;
  name?: string | null;
  slogan?: string | null;
  description?: string | null;
  crestColor?: string | null;
  logo?: string | null;
  banner?: string | null;
  kingUserId: string;
  memberLimit: number;
  level?: number;
  levelPoints?: number;
  nextLevelPoints?: number | null;
  effectiveMemberLimit?: number;
  memberCount: number;
  status: ClanStatus;
  createdAt: string;
  members: ClanMember[];
}

export interface ClanSummary {
  id: string;
  name?: string | null;
  slogan?: string | null;
  crestColor?: string | null;
  logo?: string | null;
  banner?: string | null;
  memberLimit: number;
  level?: number;
  levelPoints?: number;
  nextLevelPoints?: number | null;
  effectiveMemberLimit?: number;
  memberCount: number;
  status: ClanStatus;
  seasonScore: number;
  rank: number; // hạng mùa (1..N); chỉ có ở leaderboard
  seasonGoal: number; // mốc điểm mục tiêu mùa → % = seasonScore / seasonGoal
}

export type ClanSeasonState = 0 | 1 | 2; // REGISTRATION=0, RUNNING=1, ENDED=2

export const CLAN_SEASON_STATE_LABEL: Record<number, string> = {
  0: 'Đang mở đăng ký',
  1: 'Đang thi đấu',
  2: 'Đã kết thúc',
};

// Trạng thái mùa hiện tại + tình trạng đăng ký của bang người gọi.
export interface ClanSeason {
  month?: string | null; // "yyyy-MM"
  state: ClanSeasonState;
  minClansToStart: number;
  registeredCount: number;
  remaining: number; // còn thiếu bao nhiêu bang nữa để khởi động (0 nếu đủ)
  startedAt?: string | null;
  endedAt?: string | null;
  myClanRegistered: boolean;
  myClanId?: string | null;
}

// Một dòng Bảng vàng năm: tổng điểm giải tích luỹ + số lần Top1.
export interface ClanYearly {
  clanId: string;
  name?: string | null;
  crestColor?: string | null;
  logo?: string | null;
  year: number;
  totalLeaguePoints: number;
  top1Count: number;
  seasonsPlayed: number;
  rank: number;
}

interface ResultResponse<T> {
  httpCode: number;
  message: string;
  data: T;
}

// Gỡ bọc ResultResponse: httpCode != 200 → ném message để UI hiện toast.
const unwrap = <T>(r: { data: ResultResponse<T> }): T => {
  const body = r.data;
  if (!body || body.httpCode !== 200) {
    throw new Error(body?.message || 'Có lỗi xảy ra');
  }
  return body.data;
};

export const listClans = () =>
  API.get<ResultResponse<ClanSummary[]>>(`${BASE}/list`).then(unwrap);

// BXH War Clan mùa: các bang xếp theo điểm mùa giảm dần, kèm rank + seasonGoal.
export const getLeaderboard = (top = 20) =>
  API.get<ResultResponse<ClanSummary[]>>(`${BASE}/leaderboard`, { params: { top } }).then(unwrap);

export const getMyClan = () =>
  API.get<ResultResponse<Clan | null>>(`${BASE}/mine`).then(unwrap);

export const getClanDetail = (clanId: string) =>
  API.get<ResultResponse<Clan | null>>(`${BASE}/detail`, { params: { clanId } }).then(unwrap);

export const createClan = (body: {
  name: string;
  slogan?: string;
  description?: string;
  crestColor?: string;
  logo?: string;
  banner?: string;
}) => API.post<ResultResponse<string>>(`${BASE}/create`, body).then(unwrap);

export const joinClan = (clanId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/join`, { clanId }).then(unwrap);

export const leaveClan = (clanId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/leave`, { clanId }).then(unwrap);

export const kickMember = (clanId: string, targetUserId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/kick`, { clanId, targetUserId }).then(unwrap);

export const setRole = (clanId: string, targetUserId: string, role: ClanRole) =>
  API.post<ResultResponse<boolean>>(`${BASE}/set-role`, { clanId, targetUserId, role }).then(unwrap);

export const expandClan = (clanId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/expand`, { clanId }).then(unwrap);

export const disbandClan = (clanId: string) =>
  API.post<ResultResponse<boolean>>(`${BASE}/disband`, { clanId }).then(unwrap);

// ===== Mùa giải + Bảng vàng năm (Slice 2A/2B — auid do gateway inject từ token) =====

// Trạng thái mùa War Clan hiện tại (đăng ký / khởi động) + bang của người gọi đã đăng ký chưa.
export const getSeasonStatus = () =>
  API.get<ResultResponse<ClanSeason>>(`${BASE}/season`).then(unwrap);

// King đăng ký bang tham gia mùa hiện tại (đủ ngưỡng → mùa chuyển "Đang thi đấu").
export const registerForSeason = () =>
  API.post<ResultResponse<ClanSeason>>(`${BASE}/register`, {}).then(unwrap);

// BXH mùa — chỉ các bang đã đăng ký mùa hiện tại.
export const getSeasonLeaderboard = (top = 20) =>
  API.get<ResultResponse<ClanSummary[]>>(`${BASE}/season-leaderboard`, { params: { top } }).then(unwrap);

// Bảng vàng năm — tổng điểm giải tích luỹ 12 mùa (mặc định năm hiện tại).
export const getYearlyLeaderboard = (year?: number) =>
  API.get<ResultResponse<ClanYearly[]>>(`${BASE}/yearly-leaderboard`, {
    params: year ? { year } : {},
  }).then(unwrap);
